/**
 * The new Helply widget on the app: whether it loads for the current account, and how it is put on the page.
 *
 * Two gates, both from config (`helplyWidget`): the environment must be provisioned (a script origin and a site
 * key), and the account must be on the `accounts` list — the launch gate, `null` meaning every account. On top of
 * that the account preference `helply-agent-ready` must be on, the same switch the legacy Helply agent used. The
 * loader and the help icon read the same answer, so the widget is never on the page for an account whose help icon
 * opens the Groove widget, nor the other way round.
 */
export const isHelplyWidgetEnabled = ({ ready, subdomain, widget }) =>
  Boolean(
    ready &&
      widget &&
      widget.scriptOrigin &&
      widget.siteKey &&
      (widget.accounts == null || widget.accounts.includes(subdomain))
  )

const LOADED = '__helplyWidgetLoaded'

/**
 * The install snippet, injected rather than written into the templates: the stub that queues commands until the
 * bundle boots, the launcher hidden through the widget's one door (the help icon is the way in), then the loader.
 * Once per page: a second call is a no-op, and the loader itself re-runs nothing.
 */
export const injectHelplyWidget = widget => {
  if (typeof window === 'undefined' || window[LOADED]) return
  window[LOADED] = true
  // The install snippet's stub: each call's arguments queued as one entry, which the loader replays in order.
  // Only put there when nothing is, and remembered, so the error handler below removes this stub and nothing else.
  let stub = null
  if (typeof window.Helply !== 'function') {
    stub = (...args) => {
      ;(window.Helply.q = window.Helply.q || []).push(args)
    }
    window.Helply = stub
  }
  window.Helply('setLauncher', 'hidden')
  const script = document.createElement('script')
  script.async = true
  script.src = `${widget.scriptOrigin}/_widget/widget.js`
  script.dataset.siteKey = widget.siteKey
  // A loader that never arrives (blocked, CDN down) would leave the stub queueing the help icon's commands for
  // good. Undo the injection instead: the help icon finds no `Helply` and falls through to the Groove widget,
  // and a later call can inject again.
  script.onerror = () => {
    script.remove()
    window[LOADED] = false
    if (stub && window.Helply === stub) delete window.Helply
  }
  document.body.appendChild(script)
}

/** Subscribe through the command queue so mounting before the widget boots is safe. */
export const subscribeHelplyUnread = (widget, listener) => {
  injectHelplyWidget(widget)
  if (typeof window === 'undefined' || typeof window.Helply !== 'function') {
    return () => {}
  }
  let active = true
  const onUnread = unread => {
    if (active) listener(unread === true)
  }
  window.Helply('on', 'unreadChange', onUnread)
  return () => {
    active = false
    // The real command replaces the loading stub, so resolve it again on cleanup.
    if (typeof window.Helply === 'function') {
      window.Helply('off', 'unreadChange', onUnread)
    }
  }
}
